
import { v4 as uuidv4 } from 'uuid';
import { Banner, BannerSize, BannerTextLine, BANNER_SIZES } from '@/types/banner';
import { generateBannerImage } from './bannerCore';

// In-memory storage for banners (in a real app, this would be a database)
let banners: Banner[] = [
  {
    id: 'abC1234',
    name: 'Summer Sale',
    size: 'medium_rectangle',
    imageUrl: 'https://picsum.photos/300/250?random=1',
    createdAt: new Date(2025, 3, 15),
    text: 'Summer Sale - Up to 50% off!',
    backgroundColor: '#ffffff',
    textColor: '#000000',
    type: 'ai',
    tags: ['summer', 'sale', 'promotion']
  },
  {
    id: 'XyZ5678',
    name: 'New Product Launch',
    size: 'leaderboard',
    imageUrl: 'https://picsum.photos/728/90?random=2',
    createdAt: new Date(2025, 3, 20),
    text: 'Introducing our newest product lineup',
    backgroundColor: '#ffffff',
    textColor: '#000000',
    type: 'manual',
    tags: ['product', 'launch', 'new']
  }
];

/**
 * Generate a short banner ID (7 characters)
 */
const generateShortId = (): string => {
  // Use alphanumeric characters
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 7; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Download a banner as an image
 */
export const downloadBanner = async (banner: Banner) => {
  try {
    console.log(`Downloading banner ${banner.id}`);
    
    // Use our banner generator to create a proper image
    const { width, height } = getBannerDimensions(banner);
    const imageData = await generateBannerImage(banner, width, height);
    
    const link = document.createElement('a');
    link.href = imageData;
    link.download = `banner-${banner.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error downloading banner:', error);
    return false;
  }
};

/**
 * Get banner dimensions
 */
const getBannerDimensions = (banner: Banner): { width: number; height: number } => {
  // Find the corresponding size configuration
  const sizeConfig = BANNER_SIZES.find(s => s.value === banner.size);
  
  if (sizeConfig) {
    return {
      width: sizeConfig.width,
      height: sizeConfig.height
    };
  }
  
  // Default dimensions
  return { width: 300, height: 250 };
};

/**
 * Delete a banner by ID
 */
export const deleteBanner = (id: string): boolean => {
  const initialLength = banners.length;
  banners = banners.filter(banner => banner.id !== id);
  return banners.length < initialLength;
};

/**
 * Get all banners
 */
export const getAllBanners = (): Banner[] => {
  return [...banners];
};

/**
 * Create a new banner
 */
export const createBanner = (
  size: BannerSize, 
  textLines: BannerTextLine[], 
  backgroundColor?: string, 
  imageFile?: File,
  options?: {
    borderStyle?: string;
    borderWidth?: number;
    borderColor?: string;
    imageSettings?: {
      width: number;
      opacity: number;
      position: { x: number; y: number };
    };
  }
) => {
  const id = generateShortId();
  
  // Convert text lines to a single string for storage
  const text = textLines.map(line => line.text).join(' ');
  
  // Create an imageUrl
  let imageUrl: string | undefined;
  
  if (imageFile) {
    imageUrl = URL.createObjectURL(imageFile);
  } else {
    // Generate a deterministic image URL based on banner ID
    const sizeConfig = BANNER_SIZES.find(s => s.value === size);
    const width = sizeConfig?.width || 300;
    const height = sizeConfig?.height || 250;
    imageUrl = `https://picsum.photos/${width}/${height}?random=${id}`;
  }
  
  const newBanner: Banner = {
    id,
    name: `Banner ${id}`,
    size,
    imageUrl,
    createdAt: new Date(),
    type: 'manual',
    tags: ['custom'],
    text,
    backgroundColor: backgroundColor || '#ffffff',
    textColor: '#000000',
    textLines,
    ...options
  };
  
  banners.push(newBanner);
  return newBanner;
};

/**
 * Get a banner by ID
 */
export const getBanner = (id: string): Banner | undefined => {
  return banners.find(banner => banner.id === id);
};
